import { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import weldingProcessSvg from "@assets/4641.jpg";
import industrialWeldingImage from "@assets/517.jpg";
import weldingSparkImage from "@assets/545.jpg";

interface AboutSectionProps {
  onContactClick: () => void;
}

const AboutSection = ({ onContactClick }: AboutSectionProps) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.2,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      id="about" 
      className={`py-20 bg-white section-reveal ${isVisible ? 'visible' : ''}`}
      ref={sectionRef}
    >
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center gap-10">
          <motion.div 
            className="md:w-1/2"
            initial={{ opacity: 0, x: -20 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <h2 className="text-3xl md:text-4xl font-montserrat font-bold text-primary mb-6">
              About Swiss Welding LLC
            </h2>
            <p className="text-lg mb-4">
              Founded on principles of excellence and precision, Swiss Welding LLC has established itself 
              as a premier welding service in Ridgefield, Connecticut and surrounding areas.
            </p>
            <p className="text-lg mb-6">
              Our team of certified welders combines traditional craftsmanship with modern techniques 
              to deliver exceptional results for both residential and commercial projects.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start">
                <div className="mr-4 text-red-600 text-2xl">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-montserrat font-semibold text-lg mb-1">Quality Craftsmanship</h3>
                  <p>Precision and attention to detail in every project</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="mr-4 text-red-600 text-2xl">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-montserrat font-semibold text-lg mb-1">Certified Experts</h3>
                  <p>Team of skilled professionals with years of experience</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="mr-4 text-red-600 text-2xl">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-montserrat font-semibold text-lg mb-1">Custom Solutions</h3>
                  <p>Tailored approaches to meet unique project needs</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="mr-4 text-red-600 text-2xl">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-montserrat font-semibold text-lg mb-1">Timely Delivery</h3>
                  <p>Commitment to deadlines and project timelines</p>
                </div>
              </div>
            </div>
            <motion.button
              onClick={onContactClick}
              className="inline-block bg-red-600 hover:bg-black text-white font-montserrat font-semibold py-3 px-8 rounded-md transition-colors shadow-md"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get in Touch
            </motion.button>
          </motion.div>
          
          <motion.div 
            className="md:w-1/2 grid grid-cols-2 gap-4"
            initial={{ opacity: 0, x: 20 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            <img 
              src={weldingSparkImage} 
              alt="Metal Welding with Sparks" 
              className="rounded-lg shadow-lg object-cover h-60" 
            />
            <img 
              src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" 
              alt="Welding Equipment" 
              className="rounded-lg shadow-lg object-cover h-60 mt-10" 
            />
            <img 
              src={weldingProcessSvg} 
              alt="Welding Metal Construction" 
              className="rounded-lg shadow-lg object-cover h-60 mt-10" 
            />
            <img 
              src={industrialWeldingImage} 
              alt="Professional Welder in Workshop" 
              className="rounded-lg shadow-lg object-cover h-60" 
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
